'use client'

import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { getCreatureDetailsHelper } from "@/app/helpers/getCreatureDetailsHelper";
import { Text, Badge, Box, Link, Flex, Image, Progress, VStack, Skeleton, useDisclosure } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import NextLink from "next/link";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { CreatureDefinitionType } from "@/app/types/creatureDefinitionType";
import { GiCupcake } from "react-icons/gi";
import { FaArrowsRotate, FaStar } from "react-icons/fa6";
import { buildGameUrl } from "@/app/helpers/buildUrlHelper";
import {
    PiSmileyFill,
    PiSmileyWinkFill,
    PiSmileyMehFill,
    PiSmileySadFill,
    PiSmileyNervousFill
} from 'react-icons/pi';
import { SwitchCreature } from "./modal/switchCreature";
import { FaHourglassStart } from 'react-icons/fa';
import { MyItemsType } from "@/app/types/myItemsType";
import { AiFillFire } from 'react-icons/ai';
import { NotificationContext } from "@/app/context/notification/notificationContext";

type Props = {
    id: string;
    type : string;
    shadow?: boolean
    isNew?: boolean
};

 export default function EggCard({id, type, shadow= false, isNew=false}: Props) {

    const {
        isMyItemsLoading,
        myItems,
        careEgg,
        removeFoodReducer
    } = useContext(GlobalDataUserContext)

    const {
        addInfo
    } = useContext(NotificationContext)

    // Si vous avez toujours besoin de setCreature pour des mises à jour manuelles
    const [egg, setEgg] = useState<MyItemsType>((myItems||[]).find(egg => egg.id === id) as MyItemsType);

    useEffect(()=>{
        setEgg((myItems||[]).find(egg => egg.id === id) as MyItemsType)
    },[myItems])
    
    const [isBurning, setIsBurning] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(()=>{
        if ( isBurning) {
            const tiemout = setTimeout(()=>{
                setIsBurning(false)
            }, 6000)

            return () => clearTimeout(tiemout)
        }
    },[isBurning])

    const burnEgg = async (id:string) => { 
        removeFoodReducer(10)
        addInfo("-"+10+"🧁", "warning")
        setIsBurning(true)
        await careEgg(id)        
    }

    return (
        <>
        <Skeleton isLoaded={!isMyItemsLoading} borderRadius="20px">
            <Box 
                className={(isNew||isBurning)?"shake heat":""}
                textAlign="center"
                position="relative"
                background={type=="LEGENDARY"?"#eba628":(type=="FLYING"?"red":"#3797d1")}  
                border={(type=="LEGENDARY"||type=="AQUATIC"||type=="FLYING")?"5px solid transparent":"3px solid white"}          
                borderRadius="20px"
                overflow={"hidden"}
                
                //boxShadow={" 0 1px 3px 0 rgb(0 0 0), 0 1px 2px 0 rgb(0 0 0 / 61%);"}
                >
                    {id  && !shadow && <Flex>
                        <Image 
                            opacity={0.75+0.25*egg.levelCare/5}
                            filter={"grayscale("+0.75+0.25*(egg.levelCare/5)+")"}
                            src={"/images/eggs_"+type+".jpg"}
                            maxW={"100%"}  
                            alt="monster"
                            loading="lazy"
                            background="transparent"
                            borderRadius="20px"
                            overflow={"hidden"}
                            minH={"206px"}
                            />
                            {isNew && 
                                <Box position="absolute" zIndex="2" bg='#00000091' color="orange" borderRadius="50%" bottom={"5px"} right={"5px"} p={1}>
                                    <FaStar fontSize={"1em"}/>
                                </Box>
                            }
                    </Flex>}
                    {shadow && <Image 
                            opacity={0.75+0.25*egg.levelCare/5}
                            filter={"grayscale("+0.75+0.25*(egg.levelCare/5)+")"}
                            src={"/images/eggs_"+type+".jpg"}
                            maxW={"100%"}  
                            alt="monster"
                            loading="lazy"
                            background="transparent"
                            borderRadius="20px"
                            overflow={"hidden"}
                            minH={"206px"}
                            />}
                <Flex 
                    position="absolute"
                    bottom={3}
                    w="full"
                    justifyContent={"center"}
                    flexDir={"column"}
                    placeItems={"center"}
                >
                    <Text 
                        background={type=="LEGENDARY"? "#eba628":(type=="AQUATIC"? "#3797d1":(type=="FLYING"?"red":"#00000099"))} 
                        px={3} 
                        pr={1}
                        py={1} 
                        fontSize={"0.9em"} 
                        borderRadius={10} 
                        fontWeight="bold" 
                        color={type=="LEGENDARY"?"white":"white"}
                        lineHeight={"normal"}
                    >
                        {type.toUpperCase()}
                    </Text>              
                </Flex>
                {!shadow && <VStack w="full" position="absolute"
                        top={0}>
                    <Flex
                        w="full"
                        px={1}
                        py={1}
                        alignItems={"center"}
                        background='linear-gradient(to bottom, #000000c4, transparent)!important'
                    >
                        <Badge 
                            w="36px"
                            h="36px"
                            opacity={egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 )?1:0.75}
                            filter={egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 )?'':'grayscale(1)'}
                            background={(egg.levelCare>3?'#5f9e6e':(egg.levelCare>1?'#d69e2e':'#e53e3e'))}
                            className={( egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 ) ) ? 'pulse-white': ''} borderRadius="50%" fontSize={"1.5em"}
                            border={egg.levelCare==5?'0px solid #4544449e':'0'}
                            onClick={() => { if( egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 ) ) burnEgg(id) }}
                            alignContent={"center"}
                            color={egg.levelCare==5?"#000000c7":"white"}
                            position="relative"
                        >
                            {( egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 ) ) && <><GiCupcake /><Badge position="absolute" bottom="0" right="0">10</Badge></>}
                            {!( egg.lastCare < (new Date().getTime() - 1000 * 60 * 60 * 24 ) ) && <AiFillFire />}
                        </Badge>
                        <Box
                            w="full"
                            border="3px solid #02020242"
                            borderLeft="0"
                        >              
                            <Progress className="eating-progress" transition={"0.5s"} background="black" colorScheme={(egg.levelCare>3)?'green':(egg.levelCare>1?'yellow':'red')} size='xs' value={egg.levelCare*20} />
                        </Box>
                    </Flex>                    
                </VStack>}
            </Box>        
        </Skeleton>
        {/*<SwitchCreature isOpen={isOpen} onClose={onClose} originalCreature={{...creature,creatureId:id}} />*/}
        </>
    )}
